// src/components/Salary/SalaryAdvance.jsx
//
// Salary -> Salary Advance.
// Read-only: rows come from two sources - Cash Expenses tagged Sub Expense
// Type "Advance" (money advanced to an employee, shown as Debit) and Salary
// Entry's "Salary Advance" credit account (amount recovered each payroll
// month, shown as Credit). Nothing is created, edited or duplicated on this
// page; it only displays what is already recorded on Cash Expenses / Salary
// Entry (server/controllers/salaryAdvanceController.js).
// Laid out exactly like Finance -> Other Ledgers (same CSS): DEBIT-normal,
// running balance = previous + debit - credit, oldest first.
import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandHoldingUsd, faSpinner, faBookOpen } from '@fortawesome/free-solid-svg-icons';
import api from '../../api';
import '../Finance/FinanceLedger.css';

const fmt = (v) => parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (iso || '');
};

const SOURCE_LABELS = {
  CASH_EXPENSE_ADVANCE: 'Cash Expense',
  NEW_EXPENSE_ADVANCE: 'New Expense',
  SALARY_ENTRY_ADVANCE: 'Salary Entry'
};

const SalaryAdvance = () => {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', search: '', sourceType: '', direction: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/companies')
      .then((res) => {
        const list = res.data || [];
        setCompanies(list);
        if (list.length > 0) setCompanyId(String(list[0].id));
      })
      .catch(() => setCompanies([]));
  }, []);

  const fetchTransactions = useCallback(() => {
    if (!companyId) { setData(null); return; }
    setLoading(true);
    setError('');
    const params = { company_id: companyId };
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    api.get('/salary-advances/ledger', { params })
      .then((res) => setData(res.data))
      .catch((err) => { setData(null); setError(err.response?.data?.error || 'Failed to load Salary Advance ledger'); })
      .finally(() => setLoading(false));
    // Only the date range is sent to the server; Type/Direction/Search below
    // filter the already-fetched result, so they must not trigger a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, filters.dateFrom, filters.dateTo]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const transactions = data?.transactions || [];

  // Type / Direction / Search narrow which already-fetched rows are shown and
  // totalled, same as Cash Ledger's filter bar (its Reference/Type/Direction
  // filters are sent to the server; here the date range is, and the rest is
  // applied to that same result set - same effect, no backend change needed).
  const visibleTransactions = transactions.filter((t) => {
    if (filters.sourceType && t.source_type !== filters.sourceType) return false;
    if (filters.direction && (t.direction || '').toUpperCase() !== filters.direction) return false;
    if (filters.search.trim()) {
      const term = filters.search.trim().toLowerCase();
      const haystack = `${t.reference_no || ''} ${t.remark || ''}`.toLowerCase();
      if (!haystack.includes(term)) return false;
    }
    return true;
  });

  const totals = visibleTransactions.reduce((acc, t) => ({
    debit: acc.debit + (parseFloat(t.debit) || 0),
    credit: acc.credit + (parseFloat(t.credit) || 0)
  }), { debit: 0, credit: 0 });

  const money = (v, cls) => (parseFloat(v || 0) > 0 ? <span className={cls}>{fmt(v)}</span> : <span className="fl-empty-val">—</span>);

  return (
    <div className="page-container cash-ledger-page">
      <header className="page-header">
        <h2 style={{ textAlign: 'left', margin: 0 }}>
          <FontAwesomeIcon icon={faHandHoldingUsd} style={{ marginRight: '8px' }} /> Salary Advance
        </h2>
        <div className="cash-ledger-header-actions">
          <label htmlFor="sa-company" style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', whiteSpace: 'nowrap' }}>Company:</label>
          <select id="sa-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="report-filter-select">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </header>

      {data && (
        <div className="cash-ledger-kpi-row">
          <div className="card cash-ledger-balance-card" style={{ display: 'flex', alignItems: 'center', padding: 'var(--spacing-lg)', gap: 'var(--spacing-xl)', marginBottom: 'var(--spacing-md)' }}>
            <div className="cash-ledger-balance-icon"><FontAwesomeIcon icon={faBookOpen} /></div>
            <div style={{ flex: 1 }}>
              <div className="cash-ledger-balance-label">
                Salary Advance Balance{companies.find((c) => String(c.id) === String(companyId)) ? ` — ${companies.find((c) => String(c.id) === String(companyId)).name}` : ''}
              </div>
              <div className="cash-ledger-balance-value" style={{ margin: 'var(--spacing-xs) 0' }}>
                Rs. {fmt(data.balance)}
              </div>
            </div>
          </div>
          <div className="cash-ledger-summary" style={{ display: 'flex', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' }}>
            <div className="card cash-ledger-total-card cash-ledger-debit-card" style={{ flex: 1, padding: 'var(--spacing-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
              <span className="cash-ledger-total-label">Total Debit</span>
              <strong className="cash-ledger-total-value">Rs. {fmt(totals.debit)}</strong>
            </div>
            <div className="card cash-ledger-total-card cash-ledger-credit-card" style={{ flex: 1, padding: 'var(--spacing-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
              <span className="cash-ledger-total-label">Total Credit</span>
              <strong className="cash-ledger-total-value">Rs. {fmt(totals.credit)}</strong>
            </div>
          </div>
        </div>
      )}

      <div className="card filters-section report-filter-bar">
        <div className="ledger-filter-row">
          <input type="date" value={filters.dateFrom} onChange={(e) => setFilters((p) => ({ ...p, dateFrom: e.target.value }))} title="Date From" className="report-filter-input" />
          <input type="date" value={filters.dateTo} onChange={(e) => setFilters((p) => ({ ...p, dateTo: e.target.value }))} title="Date To" className="report-filter-input" />
          <input type="text" value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} placeholder="Reference / Remark" className="report-filter-input" />
          <select value={filters.sourceType} onChange={(e) => setFilters((p) => ({ ...p, sourceType: e.target.value }))} className="report-filter-select">
            <option value="">All Types</option>
            {Object.entries(SOURCE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select value={filters.direction} onChange={(e) => setFilters((p) => ({ ...p, direction: e.target.value }))} className="report-filter-select">
            <option value="">All Directions</option>
            <option value="IN">IN</option>
            <option value="OUT">OUT</option>
          </select>
        </div>
      </div>

      {error && <div className="sl-modal-error" role="alert" style={{ marginBottom: 'var(--spacing-md)' }}>{error}</div>}

      <div className="card cash-ledger-table-card other-ledger-table-card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 'var(--spacing-2xl)', color: 'var(--color-text-secondary)' }}><FontAwesomeIcon icon={faSpinner} spin /> Loading…</div>
        ) : (
          <table className="report-table cash-ledger-table other-ledger-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Company</th>
                <th>Type</th>
                <th>Direction</th>
                <th>Reference</th>
                <th>Remark</th>
                <th className="fl-num">Debit</th>
                <th className="fl-num">Credit</th>
                <th className="fl-num">Balance</th>
              </tr>
            </thead>
            <tbody>
              {data && Number(data.opening_balance) !== 0 && (
                <tr>
                  <td colSpan={8} style={{ fontStyle: 'italic' }}>Balance brought forward</td>
                  <td className="fl-num fl-balance-cell">{fmt(data.opening_balance)}</td>
                </tr>
              )}
              {visibleTransactions.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 'var(--spacing-2xl)', color: 'var(--color-text-secondary)' }}>
                  {!companyId ? 'Select a company' : transactions.length === 0 ? 'No transactions yet' : 'No transactions match the current filters'}
                </td></tr>
              ) : visibleTransactions.map((t) => (
                <tr key={t.id}>
                  <td>{fmtDate(t.date)}</td>
                  <td>{t.company_name || '-'}</td>
                  <td><span className="fl-type-chip">{SOURCE_LABELS[t.source_type] || t.source_type}</span></td>
                  <td>
                    {t.direction ? (
                      <span className={`fl-direction fl-direction-${t.direction.toLowerCase()}`}>
                        {t.direction === 'IN' ? '▲' : '▼'} {t.direction}
                      </span>
                    ) : <span className="fl-empty-val">—</span>}
                  </td>
                  <td>{t.reference_no || '—'}</td>
                  <td>
                    <span className="fl-remark-text">{t.remark || '—'}</span>
                    {t.expense_type && t.sub_expense_type && (
                      <small style={{ display: 'block', color: 'var(--color-text-secondary)' }}>
                        {t.expense_type} / {t.sub_expense_type}
                      </small>
                    )}
                  </td>
                  <td className="fl-num">{money(t.debit, 'fl-debit-amount')}</td>
                  <td className="fl-num">{money(t.credit, 'fl-credit-amount')}</td>
                  <td className="fl-num fl-balance-cell">{fmt(t.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SalaryAdvance;
